import {useCurrentRepository} from '@github-ui/current-repository'
import {deferredRichTextPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import type {RefInfo} from '@github-ui/repos-types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import React, {useMemo} from 'react'

interface DeferredRichText {
  richText: SafeHTMLString | null
}

const EmptyDeferredRichText: DeferredRichText = {
  richText: null,
}

const DeferredRichTextContext = React.createContext<DeferredRichText>(EmptyDeferredRichText)

export function DeferredRichTextProvider({children, richText}: React.PropsWithChildren<DeferredRichText>) {
  const value = useMemo<DeferredRichText>(() => ({richText}), [richText])
  return <DeferredRichTextContext value={value}>{children}</DeferredRichTextContext>
}

export function useDeferredRichText() {
  return React.use(DeferredRichTextContext)
}

async function fetchDeferredRichText(url: string | null): Promise<DeferredRichText> {
  if (!url) return EmptyDeferredRichText

  const response = await verifiedFetchJSON(url)

  if (response.ok) {
    const data = await response.json()
    if (data && typeof data.richText === 'string') {
      return data as DeferredRichText
    }
  }

  return EmptyDeferredRichText
}

export function useDeferredRichTextQuery(
  refInfo: RefInfo | undefined,
  path: string | undefined,
  richTextTruncated: boolean,
): DeferredRichText {
  const repo = useCurrentRepository()
  const url =
    refInfo && path && richTextTruncated
      ? deferredRichTextPath({
          repo,
          commitish: refInfo.name,
          path,
        })
      : null

  const enabled = Boolean(url)

  const {data} = useQuery({
    queryKey: ['deferredRichText', repo.ownerLogin, repo.name, refInfo?.name, path, url],
    queryFn: () => fetchDeferredRichText(url),
    enabled,
    placeholderData: EmptyDeferredRichText,
  })

  return enabled ? (data ?? EmptyDeferredRichText) : EmptyDeferredRichText
}
